import React, { type JSXElementConstructor } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import * as R from 'ramda'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import {
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/components/dashboard/invoices/invoiceUtils'
import { OrderComponentsActions } from '~/components/dashboard/soapV2/collapsibleRail/summary/orders/orderSummaryUtils'
import type { OrderType } from '~/constants/SOAPStates'
import { getChargesLoading } from '~/store/duck/charges'
import { useIsDeclinableOrDeletable } from '~/store/hooks/prescription'
import { getSelectedOrder } from '~/store/reducers/orders'
import type { InvoiceLineItem, Order } from '~/types'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
} from '~/utils/orderUtils'

import OrderItemButtonStatus from '../utils/OrderItemButtonStatus'
import { useChargeInformationPanelPermissions } from '../utils/useChargeInformationPanelPermissions'
import {
  useDeclineLineItem,
  useDeclineOrderItem,
  useGetIsDeclinedLineItem,
} from '../utils/useDeclineOrderItem'
import ChargeBundleItemButtonsActions from './ChargeBundleItemButtonsActions'

export interface ChargeItemButtonActionsProps {
  ComponentsActions?: Partial<Record<OrderType, JSXElementConstructor<any>>>
  disabled: boolean
  isInvoice?: boolean
  isLoading: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  onDelete: () => void
  onUpdateItem: (item: InvoiceLineItem | RetailOrderLineItem) => void
}

const ChargeItemButtonActions = ({
  disabled,
  item,
  onDelete,
  onUpdateItem,
  ComponentsActions,
  isInvoice,
  isLoading = false,
}: ChargeItemButtonActionsProps) /* NOSONAR */ => {
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
  const soapOrder = useSelector(
    getSelectedOrder(item.logType as OrderType, item.logId),
  )
  const isDeclined = useGetIsDeclinedLineItem(item)

  const chargesLoading = useSelector(getChargesLoading)

  const order = soapOrder
  const convertedToOrderItem = !soapOrder
    ? isRetailOrderLineItem
      ? convertRetailOrderLineItemToSoapLog(item)
      : convertInvoiceLineItemToSoapLog(item)
    : ({} as Order)

  const type =
    order?.type ||
    (item.logType as OrderType) ||
    (!isRetailOrderLineItem ? item.type : undefined)

  const onDeclineOrder = useDeclineOrderItem(order)
  const onDeclineLineItem = useDeclineLineItem(item)
  const isDeclinableOrDeletable = useIsDeclinableOrDeletable({
    orderType: order?.type || convertedToOrderItem?.type,
    stateId: order?.stateId || convertedToOrderItem?.stateId,
    prescriptionType:
      order?.prescriptionType || convertedToOrderItem?.prescriptionType,
    origin: order?.origin || convertedToOrderItem?.origin,
  })

  const IconItemComponent =
    (ComponentsActions && ComponentsActions[type]) ||
    OrderComponentsActions[type]

  const isPrepaidAndUsedZero = getIsRetailOrderLineItem(item) // RetailOrderLineItem has no concept of prepaid.
    ? false
    : Boolean(item.prepaid) && item.usedQuantity === 0

  const permissions = useChargeInformationPanelPermissions(
    item,
    isInvoice,
    isPrepaidAndUsedZero,
  )

  if (isGroupedInvoiceItem(item) && !isRetailOrderLineItem) {
    return (
      <ChargeBundleItemButtonsActions
        disabled={disabled}
        item={item}
        loading={isLoading ? false : chargesLoading}
        onDelete={onDelete}
        onUpdateItem={onUpdateItem}
      />
    )
  }

  if (!order && !item.logType && (isRetailOrderLineItem ? true : !item.type)) {
    return null
  }

  return (
    <Grid container>
      {!isPrepaidAndUsedZero && (
        <Grid item data-testid="charge-item-button" mb={1}>
          <OrderItemButtonStatus
            disabled={R.path(['Status', 'disabled'], permissions)}
            order={item}
          />
        </Grid>
      )}
      {IconItemComponent && (
        <IconItemComponent
          hideDivider
          align="flex-start"
          declined={isDeclined}
          isInvoice={isInvoice}
          isLineItem={Boolean(ComponentsActions)}
          isPrepaidAndUsedZero={isPrepaidAndUsedZero}
          isRetailOrderLineItem={isRetailOrderLineItem}
          loading={isLoading ? false : chargesLoading}
          order={item}
          permissions={ComponentsActions ? permissions : null}
          readOnly={disabled}
          size="large"
          onDecline={
            disabled || !isDeclinableOrDeletable
              ? undefined
              : !ComponentsActions
                ? onDeclineOrder
                : onDeclineLineItem
          }
          onDelete={isDeclinableOrDeletable ? onDelete : undefined}
          onUpdateNotes={undefined}
        />
      )}
    </Grid>
  )
}

export default ChargeItemButtonActions
